/**
 * AdminNotifyInterestTab — inbox for notify-interest leads.
 *
 * Wave 5-Late Marketplace propagation. Same executive discipline as
 * AdminConsultationsTab: Section chrome + hairlines, dense rows,
 * muted Badge tones with `shape="sharp"`, `overflow-x-auto`, close-
 * reason confirm panel restyled to admin card chrome.
 *
 * CP3.5 close-reason confirm sub-panel + 422 guardrail error state +
 * 5xx retryable path all preserved BYTE-EXACT. Testids unchanged.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Inbox, Loader2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { Heading } from "@/design-system/components/Heading";
import { Badge } from "@/design-system/components/Badge";
import { AccentButton, GhostButton, Section, SelectInput } from "@/admin/_primitives";

const STATUS_FILTERS = ["all", "open", "closed"];
const CLOSED_REASON_MAX = 200;

const STATUS_TONE = {
  open: "info",
  closed: "neutralMuted",
};

function formatDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return s;
  }
}

async function copyToClipboard(text) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied.");
  } catch {
    toast.error("Copy failed.");
  }
}

/* ------------------------------------------------------------------ */
/* Local admin primitives.                                              */
/* ------------------------------------------------------------------ */


function Field({ label, children }) {
  return (
    <label className="block">
      <span
        className="block text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}




const GRID_COLS = "grid-cols-[130px_1fr_1fr_1fr_90px]";

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function AdminNotifyInterestTab({ categories }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("all");
  const [categorySlug, setCategorySlug] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const [closeOpen, setCloseOpen] = useState(false);
  const [closeReason, setCloseReason] = useState("");
  const [closeSubmitting, setCloseSubmitting] = useState(false);
  const [closeError, setCloseError] = useState(null);
  const [closeErrorCode, setCloseErrorCode] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 200 };
      if (status !== "all") params.status = status;
      if (categorySlug) params.category_slug = categorySlug;
      const { data } = await api.get("/admin/marketplace/notify-interest-leads", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(formatApiError(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, categorySlug]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const catOpts = useMemo(
    () => (categories || []).slice().sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [categories],
  );
  const selected = useMemo(
    () => rows.find((r) => r.id === selectedId) || null,
    [rows, selectedId],
  );

  useEffect(() => {
    setCloseOpen(false);
    setCloseReason("");
    setCloseError(null);
    setCloseErrorCode(null);
    setCloseSubmitting(false);
  }, [selectedId]);

  async function submitClose() {
    if (!selected) return;
    const reason = closeReason.trim();
    if (!reason) {
      setCloseError("Reason is required.");
      setCloseErrorCode("client_required");
      return;
    }
    setCloseSubmitting(true);
    setCloseError(null);
    setCloseErrorCode(null);
    try {
      const { data } = await api.patch(
        `/admin/marketplace/notify-interest-leads/${selected.id}`,
        { action: "close", closed_reason: reason },
      );
      setRows((rs) => rs.map((r) => (r.id === selected.id ? data : r)));
      setCloseOpen(false);
      setCloseReason("");
      toast.success("Lead closed.");
    } catch (err) {
      const httpStatus = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (httpStatus === 422 && code === "notify_close_reason_failed_guardrail") {
        setCloseError("Please rephrase and try again.");
        setCloseErrorCode(code);
      } else if (httpStatus >= 500) {
        setCloseError(formatApiError(err) || "Something went wrong. Please try again.");
        setCloseErrorCode("retryable");
      } else {
        setCloseError(formatApiError(err) || "Close failed.");
        setCloseErrorCode("generic");
      }
    } finally {
      setCloseSubmitting(false);
    }
  }

  const filterBadge = (
    <GhostButton onClick={fetchRows} data-testid="admin-notify-refresh" className="!py-1 !text-[11px]">
      <RefreshCw className="w-3 h-3" /> Refresh
    </GhostButton>
  );

  return (
    <div>
      <Section title="Filter notify-interest" icon={Search} badge={filterBadge} testid="admin-notify-filters">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Status">
            <SelectInput
              data-testid="admin-notify-status-filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Category">
            <SelectInput
              data-testid="admin-notify-category-filter"
              value={categorySlug}
              onChange={(e) => setCategorySlug(e.target.value)}
            >
              <option value="">All categories</option>
              {catOpts.map((c) => (
                <option key={c.slug} value={c.slug}>{c.display_name}</option>
              ))}
            </SelectInput>
          </Field>
        </div>
      </Section>

      {error && (
        <div
          className="mb-3 rounded-[var(--ds-radius-field)] border px-3 py-2 text-[12px]"
          style={{
            borderColor: "var(--ds-color-danger)",
            color: "var(--ds-color-danger)",
            background: "var(--ds-color-danger-soft)",
          }}
        >
          {error}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-[1fr_360px]">
        <Section
          title="Notify-interest leads"
          icon={Inbox}
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {rows.length} total
            </span>
          }
          testid="admin-notify-list-section"
          bodyClassName="p-0"
        >
          <div className="overflow-x-auto" data-testid="admin-notify-list">
            <div
              className={`grid ${GRID_COLS} gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.16em] font-semibold`}
              style={{
                color: "var(--ds-color-ink-subtle)",
                background: "var(--ds-color-admin-surface-muted)",
                borderBottom: "1px solid var(--ds-color-admin-border)",
              }}
            >
              <span>Created</span>
              <span>User</span>
              <span>Target</span>
              <span>Note</span>
              <span>Status</span>
            </div>
            {loading ? (
              <div
                className="px-4 py-8 text-[13px] text-center"
                style={{ color: "var(--ds-color-ink-muted)" }}
                data-testid="admin-notify-loading"
              >
                Loading…
              </div>
            ) : rows.length === 0 ? (
              <div
                className="px-4 py-10 text-[13px] text-center"
                style={{ color: "var(--ds-color-ink-muted)" }}
                data-testid="admin-notify-empty"
              >
                No notify-interest leads match those filters.
              </div>
            ) : (
              rows.map((r, idx) => {
                const target = r.professional_name
                  ? r.professional_name
                  : r.category_slug
                    ? `Category · ${r.category_slug.replace(/_/g, " ")}`
                    : "—";
                const isLast = idx === rows.length - 1;
                const isSelected = selectedId === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    data-testid="admin-notify-list-row"
                    data-lead-id={r.id}
                    data-status={r.status}
                    onClick={() => setSelectedId(r.id)}
                    className={`w-full text-left grid ${GRID_COLS} gap-3 px-4 py-1.5 text-[13px] items-center transition-colors`}
                    style={{
                      borderBottom: isLast
                        ? "none"
                        : "1px solid var(--ds-color-admin-border)",
                      background: isSelected
                        ? "var(--ds-color-accent-soft)"
                        : "transparent",
                      transitionDuration: "var(--ds-duration-fast)",
                    }}
                  >
                    <span className="text-[12px] tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>
                      {formatDate(r.created_at)}
                    </span>
                    <span className="truncate" style={{ color: "var(--ds-color-ink)" }}>{r.user_display_name || "—"}</span>
                    <span className="truncate" style={{ color: "var(--ds-color-ink)" }}>{target}</span>
                    <span className="truncate text-[12px]" style={{ color: "var(--ds-color-ink-muted)" }}>{r.note || "—"}</span>
                    <Badge
                      tone={STATUS_TONE[r.status] || "neutralMuted"}
                      size="sm"
                      shape="sharp"
                      className="uppercase tracking-[0.14em] w-fit"
                    >
                      {r.status}
                    </Badge>
                  </button>
                );
              })
            )}
          </div>
        </Section>

        <Section title="Details" testid="admin-notify-detail-section" bodyClassName="p-4">
          <div data-testid="admin-notify-detail" className="space-y-3">
            {!selected ? (
              <div className="text-[13px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                Select a row to see full lead details.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2">
                  <Heading level={4} size="sm" weight="semibold" className="min-w-0 truncate">
                    {selected.professional_name || `Category · ${(selected.category_slug || "").replace(/_/g, " ")}`}
                  </Heading>
                  <Badge
                    tone={STATUS_TONE[selected.status] || "neutralMuted"}
                    size="sm"
                    shape="sharp"
                    className="uppercase tracking-[0.14em]"
                  >
                    {selected.status}
                  </Badge>
                </div>
                <div className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                  Submitted {formatDate(selected.created_at)}
                </div>
                <div>
                  <div
                    className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                    style={{ color: "var(--ds-color-ink-subtle)" }}
                  >
                    User
                  </div>
                  <div className="text-[13px]" style={{ color: "var(--ds-color-ink)" }}>
                    {selected.user_display_name || "—"}
                  </div>
                  {selected.user_contact_email && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                        {selected.user_contact_email}
                      </span>
                      <button
                        type="button"
                        data-testid="admin-notify-copy-user-email"
                        onClick={() => copyToClipboard(selected.user_contact_email)}
                        className="text-[11px] inline-flex items-center gap-0.5 hover:underline"
                        style={{ color: "var(--ds-color-accent-strong)" }}
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                  )}
                </div>
                {selected.note && (
                  <div>
                    <div
                      className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1"
                      style={{ color: "var(--ds-color-ink-subtle)" }}
                    >
                      Note
                    </div>
                    <p className="text-[13px] whitespace-pre-wrap" style={{ color: "var(--ds-color-ink)" }}>
                      {selected.note}
                    </p>
                  </div>
                )}

                {selected.status === "closed" ? (
                  <div
                    data-testid="admin-notify-closed-summary"
                    className="rounded-[var(--ds-radius-input)] p-3 text-[11px] space-y-1"
                    style={{
                      background: "var(--ds-color-admin-surface-muted)",
                      border: "1px solid var(--ds-color-admin-border)",
                      color: "var(--ds-color-ink)",
                    }}
                  >
                    <div
                      className="text-[10.5px] uppercase tracking-[0.16em] font-semibold"
                      style={{ color: "var(--ds-color-ink-subtle)" }}
                    >
                      Closed
                    </div>
                    <div>
                      <span style={{ color: "var(--ds-color-ink-muted)" }}>At:</span> {formatDate(selected.closed_at)}
                    </div>
                    {selected.closed_reason && (
                      <div>
                        <span style={{ color: "var(--ds-color-ink-muted)" }}>Reason:</span> {selected.closed_reason}
                      </div>
                    )}
                  </div>
                ) : closeOpen ? (
                  <div
                    data-testid="admin-notify-close-panel"
                    className="rounded-[var(--ds-radius-input)] p-3 space-y-2"
                    style={{
                      background: "var(--ds-color-admin-surface-muted)",
                      border: "1px solid var(--ds-color-admin-border)",
                    }}
                  >
                    <label
                      className="text-[10.5px] uppercase tracking-[0.16em] font-semibold block"
                      style={{ color: "var(--ds-color-ink-subtle)" }}
                    >
                      Close reason (required, ≤ {CLOSED_REASON_MAX} chars)
                    </label>
                    <textarea
                      data-testid="admin-notify-close-reason"
                      className="w-full rounded-[var(--ds-radius-input)] border bg-[var(--ds-color-surface)] px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-[var(--ds-color-input-border-strong)]"
                      style={{
                        borderColor: closeError
                          ? "var(--ds-color-danger)"
                          : "var(--ds-color-input-border)",
                        color: "var(--ds-color-ink)",
                        minHeight: 64,
                      }}
                      maxLength={CLOSED_REASON_MAX}
                      value={closeReason}
                      onChange={(e) => {
                        setCloseReason(e.target.value);
                        setCloseError(null);
                        setCloseErrorCode(null);
                      }}
                      placeholder="Short internal note for the audit trail."
                    />
                    <div
                      data-testid="admin-notify-close-reason-counter"
                      className="text-[10px] text-right tabular-nums"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      {closeReason.length} / {CLOSED_REASON_MAX}
                    </div>
                    {closeError && (
                      <div
                        data-testid="admin-notify-close-error"
                        data-error-code={closeErrorCode || ""}
                        role="alert"
                        className="rounded-[var(--ds-radius-input)] p-2 text-[11px] flex items-start justify-between gap-2"
                        style={{
                          background: "var(--ds-color-danger-soft)",
                          border: "1px solid var(--ds-color-danger)",
                          color: "var(--ds-color-danger)",
                        }}
                      >
                        <span>{closeError}</span>
                        {closeErrorCode === "retryable" && (
                          <button
                            type="button"
                            data-testid="admin-notify-close-retry"
                            onClick={submitClose}
                            className="text-[10px] font-semibold underline"
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    )}
                    <div className="flex gap-2 justify-end pt-1">
                      <GhostButton
                        data-testid="admin-notify-close-cancel"
                        onClick={() => {
                          setCloseOpen(false);
                          setCloseReason("");
                          setCloseError(null);
                          setCloseErrorCode(null);
                        }}
                        disabled={closeSubmitting}
                        className="!py-1 !text-[11px]"
                      >
                        Cancel
                      </GhostButton>
                      <AccentButton
                        data-testid="admin-notify-close-submit"
                        onClick={submitClose}
                        disabled={closeSubmitting || !closeReason.trim()}
                        className="!py-1 !text-[11px]"
                      >
                        {closeSubmitting && <Loader2 className="w-3 h-3 animate-spin" />}
                        {closeSubmitting ? "Closing…" : "Confirm close"}
                      </AccentButton>
                    </div>
                  </div>
                ) : (
                  <AccentButton
                    data-testid="admin-notify-close"
                    onClick={() => setCloseOpen(true)}
                    className="w-full justify-center"
                  >
                    Mark closed
                  </AccentButton>
                )}
              </>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}
